import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `/api`;

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product_interest: "",
    message: ""
  });

  useEffect(() => {
    // Pre-fill product interest from URL params
    const product = searchParams.get("product");
    if (product) {
      setFormData(prev => ({
        ...prev,
        product_interest: product,
        message: `I am interested in ${product}. Please provide more information including pricing and availability.`
      }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Thank you! Your inquiry has been submitted. We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        product_interest: "",
        message: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit inquiry");
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Helmet>
        <title>Contact DUGARS | Get a Quote</title>
        <meta name="description" content="Get in touch with DUGARS for fabric inquiries, quotes, and orders. Located in Howrah, West Bengal. Call +91 6360372623." />
        <link rel="canonical" href="https://thedugars.in/contact" />
        <meta property="og:title" content="Contact DUGARS | Get a Quote" />
        <meta property="og:description" content="Get in touch for fabric inquiries, quotes, and orders." />
        <meta property="og:url" content="https://thedugars.in/contact" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact DUGARS | Get a Quote" />
        <meta name="twitter:description" content="Get in touch for fabric inquiries, quotes, and orders." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "DUGARS",
          "telephone": "+916360372623",
          "email": "info@thedugars.in",
          "address": { "@type": "PostalAddress", "streetAddress": "26, Jagat Banerjee Ghat Rd, Choura Bustee, Shibpur", "addressLocality": "Howrah", "addressRegion": "West Bengal", "postalCode": "711102", "addressCountry": "IN" },
          "openingHours": ["Mo-Fr 09:00-18:00", "Sa 10:00-16:00"]
        })}</script>
      </Helmet>
      {/* Header */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#4A5D4E] mb-4 block">
              Get in Touch
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#1C1C1A]">
              Let's Talk Fabrics
            </h1>
            <p className="text-[#6B6A65] font-light mt-6">
              Have questions about our collections? Looking for specific fabrics? 
              Fill out the form below and we'll get back to you promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-12 border border-[#E6E4DD]">
                <h2 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-normal text-[#1C1C1A] mb-8">
                  Request a Quote
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="custom-input w-full"
                        placeholder="Your name"
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="custom-input w-full"
                        placeholder="your@email.com"
                        required
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="custom-input w-full"
                        placeholder="+91 XXXXXXXXXX"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="custom-input w-full"
                        placeholder="Your company name"
                        data-testid="input-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                      Product Interest
                    </label>
                    <select
                      name="product_interest"
                      value={formData.product_interest}
                      onChange={handleChange}
                      className="custom-input w-full"
                      data-testid="select-product-interest"
                    >
                      <option value="">Select a collection (optional)</option>
                      <option value="50s SLUB Pique">50s SLUB Pique</option>
                      <option value="50s NR Pique">50s NR Pique</option>
                      <option value="60s NR HERRINGBONE">60s NR HERRINGBONE</option>
                      <option value="BOSS DOBBY">BOSS DOBBY</option>
                      <option value="50s NR PLAIN DYED">50s NR PLAIN DYED</option>
                      <option value="All Collections">All Collections</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#1C1C1A] mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="custom-input w-full resize-none"
                      placeholder="Tell us about your requirements..."
                      required
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="inquiry-submit-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Inquiry
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5">
              <div className="space-y-8">
                {/* Direct Contact */}
                <div className="bg-white p-8 border border-[#E6E4DD]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1C1C1A] mb-6">
                    Direct Contact
                  </h3>
                  <div className="space-y-4">
                    <a 
                      href="tel:+916360372623"
                      className="flex items-center gap-4 p-4 border border-[#E6E4DD] hover:border-[#4A5D4E] transition-colors group"
                      data-testid="contact-phone"
                    >
                      <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] group-hover:bg-[#4A5D4E] transition-colors">
                        <Phone size={20} className="text-[#4A5D4E] group-hover:text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-[#6B6A65] uppercase tracking-wide mb-1">Phone</p>
                        <p className="text-[#1C1C1A] font-medium">+91 6360372623</p>
                      </div>
                    </a>

                    {/* Replace with actual domain email when mail server is configured */}
                    <a 
                      href="mailto:info@thedugars.in"
                      className="flex items-center gap-4 p-4 border border-[#E6E4DD] hover:border-[#4A5D4E] transition-colors group"
                      data-testid="contact-email"
                    >
                      <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] group-hover:bg-[#4A5D4E] transition-colors">
                        <Mail size={20} className="text-[#4A5D4E] group-hover:text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-[#6B6A65] uppercase tracking-wide mb-1">Email</p>
                        <p className="text-[#1C1C1A] font-medium">info@thedugars.in</p>
                      </div>
                    </a>

                    <a 
                      href="https://maps.google.com/?q=26,+Jagat+Banerjee+Ghat+Rd,+Choura+Bustee,+Shibpur,+Howrah,+West+Bengal+711102"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 border border-[#E6E4DD] hover:border-[#4A5D4E] transition-colors group"
                      data-testid="contact-address"
                    >
                      <div className="w-12 h-12 flex items-center justify-center bg-[#F7F5F0] group-hover:bg-[#4A5D4E] transition-colors">
                        <MapPin size={20} className="text-[#4A5D4E] group-hover:text-white" />
                      </div>
                      <div>
                        <p className="text-xs text-[#6B6A65] uppercase tracking-wide mb-1">Location</p>
                        <p className="text-[#1C1C1A] font-medium text-sm">
                          26, Jagat Banerjee Ghat Rd,<br />
                          Choura Bustee, Shibpur,<br />
                          Howrah, West Bengal 711102
                        </p>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Google Map */}
                <div className="bg-white border border-[#E6E4DD] overflow-hidden relative" style={{ minHeight: "250px" }}>
                  <div className="absolute inset-0 flex items-center justify-center bg-[#F7F5F0] text-[#6B6A65] text-sm z-0">Loading map...</div>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3684.5468372647!2d88.30908127596!3d22.559661633567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a0279c7e8d0c1a1%3A0x8b0e5a3b1c7d8e9f!2s26%2C%20Jagat%20Banerjee%20Ghat%20Rd%2C%20Choura%20Bustee%2C%20Shibpur%2C%20Howrah%2C%20West%20Bengal%20711102!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin"
                    width="100%"
                    height="250"
                    style={{ border: 0, position: "relative", zIndex: 1 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="DUGARS Location"
                    data-testid="contact-map"
                  />
                </div>

                {/* Business Hours */}
                <div className="bg-white p-8 border border-[#E6E4DD]">
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#1C1C1A] mb-6">
                    Business Hours
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#6B6A65]">Monday - Friday</span>
                      <span className="text-[#1C1C1A]">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B6A65]">Saturday</span>
                      <span className="text-[#1C1C1A]">10:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B6A65]">Sunday</span>
                      <span className="text-[#1C1C1A]">Closed</span>
                    </div>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="bg-[#1C1C1A] p-8">
                  <h3 className="font-['Cormorant_Garamond'] text-xl font-normal text-[#F7F5F0] mb-4">
                    Prefer WhatsApp?
                  </h3>
                  <p className="text-[#F7F5F0]/70 text-sm mb-6">
                    Chat with us directly for quick responses and instant support.
                  </p>
                  <a
                    href="https://wa.me/916360372623?text=Hello!%20I'm%20interested%20in%20your%20fabric%20collections."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#4A5D4E] text-white text-sm font-medium uppercase tracking-wide hover:bg-[#3A4A3E] transition-colors"
                    data-testid="whatsapp-link"
                  >
                    Chat on WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
